/**
 * AEManualWindows — экспорт «Ручной настройки» /audio-enhancer окнами.
 *
 * Зачем. Экспорт ручного режима рендерил запись целиком: раскодированный
 * буфер + OfflineAudioContext на всю длину + WAV. Записи, чей PCM не влезал
 * в бюджет декода, живая предпрослушка пропускала (и говорила: «сам экспорт
 * выполнится»), а экспорт начинался со строки `if (!manualBuffer) return` —
 * кнопка «Скачать результат» молчала (чат Премиума 686, 14.10.2026: запись
 * звонка 48 минут, 20+ нажатий на двух компьютерах, человек сам разрезал
 * запись на четыре части).
 *
 * Как. Тот же граф Web Audio, что звучит в предпрослушке, рендерится окнами
 * с прероллом впереди (узлам с памятью хватает его, чтобы забыть всё до
 * окна) и на задержку компрессора длиннее (хвост окна посчитан против
 * настоящего входа). Вход — либо уже раскодированный буфер, либо блоки по
 * 30 с прямо из файла (AudioRangeSource: файл смонтирован в FFmpeg, в память
 * не копируется).
 * Память — одно окно, а не запись. Образцы — `renderMixWindows` в /audio-mixer
 * и `SpeechRestorerEngine`; разбор — docs/why/audio-enhancer-manual-export.md.
 */
(function (root) {
    'use strict';

    /* Компрессор Chromium обновляет усиление долями от ПЕРВОГО отсчёта
       контекста: начало окна вне сетки 128 сжимало бы по сдвинутой сетке. */
    var GRID = 128;
    var FLOOR_FRAME = 2048;
    /* Блоки на целых секундах: стыки посэмплово равны непрерывному декоду
       (AudioRangeSource читает с лид-ином в секунду). */
    var READ_BLOCK_S = 30;

    function abortErr() {
        var e = new Error('cancelled');
        e.name = 'AbortError';
        e.code = 'USER_CANCELLED';
        return e;
    }

    /** Окно [s, e) таймлайна: откуда брать вход и сколько рендерить.
     *  `from` — начало входа (с прероллом, на сетке 128), `frames` — длина
     *  рендера (на `cut` длиннее), `skip` — с какого отсчёта рендера читать. */
    function windowAt(s, e, pre, cut) {
        var from = Math.max(0, Math.floor((s - pre) / GRID) * GRID);
        return { s: s, e: e, from: from, frames: (e - from) + cut, skip: (s - from) + cut };
    }

    /** Все окна записи известной длины (тесты и источник-буфер). */
    function plan(total, win, pre, cut) {
        var out = [];
        for (var s = 0; s < total; s += win) out.push(windowAt(s, Math.min(total, s + win), pre, cut));
        return out;
    }

    /** Сколько прошлого нужно узлам графа, чтобы забыть всё до окна: бикуады
     *  затухают за миллисекунды, компрессор — 1 с + шесть его отпусканий. */
    function prerollSec(releaseSec) {
        var r = isFinite(releaseSec) && releaseSec > 0 ? releaseSec : 0.25;
        return Math.min(30, 1 + 6 * r);
    }

    /** Пол шума отрендеренного сигнала — та же арифметика, что считалась по
     *  целому буферу (кадры 2048 по каналу 0, 5-й и 70-й процентили RMS), но
     *  накопителем: окна приходят по одному, остаток кадра переносится. */
    function FloorStats() {
        var carry = new Float32Array(FLOOR_FRAME), fill = 0, rms = [];
        return {
            push: function (x) {
                var i = 0, n = x.length;
                while (i < n) {
                    var take = Math.min(FLOOR_FRAME - fill, n - i);
                    carry.set(x.subarray(i, i + take), fill);
                    fill += take; i += take;
                    if (fill === FLOOR_FRAME) {
                        var s = 0;
                        for (var k = 0; k < FLOOR_FRAME; k++) s += carry[k] * carry[k];
                        rms.push(Math.sqrt(s / FLOOR_FRAME));
                        fill = 0;
                    }
                }
            },
            stats: function () {
                if (rms.length < 40) return null;
                var a = rms.slice().sort(function (p, q) { return p - q; });
                var floorDb = 20 * Math.log10(Math.max(1e-7, a[Math.floor(a.length * 0.05)]));
                var refDb = 20 * Math.log10(Math.max(1e-7, a[Math.floor(a.length * 0.70)]));
                return { floorDb: floorDb, refDb: refDb, reliable: (refDb - floorDb) >= 8 };
            },
            frames: function () { return rms.length; }
        };
    }

    /* ── Источники ────────────────────────────────────────────────────────
       fill(upTo)   — дочитать вход до отсчёта upTo (или до конца записи);
       total()      — длина записи в отсчётах, null, пока конец не встречен;
       slice(a, b)  — по массиву на канал, отсчёты [a, b);
       drop(before) — отсчёты раньше before больше не понадобятся. */

    function bufferSource(audioBuffer) {
        var chans = [];
        for (var c = 0; c < audioBuffer.numberOfChannels; c++) chans.push(audioBuffer.getChannelData(c));
        var len = audioBuffer.length;
        return {
            kind: 'buffer', sampleRate: audioBuffer.sampleRate, channels: chans.length,
            fill: function () { return Promise.resolve(); },
            total: function () { return len; },
            slice: function (a, b) {
                a = Math.max(0, Math.min(len, a)); b = Math.max(a, Math.min(len, b));
                return chans.map(function (d) { return d.subarray(a, b); });
            },
            drop: function () {},
            close: function () { chans = []; return Promise.resolve(); }
        };
    }

    /** Файл через AudioRangeSource: читается блоками по 30 с на целых
     *  секундах; в памяти — только то, что ещё нужно окнам. Длина записи
     *  узнаётся по короткому блоку. */
    async function ffmpegSource(file, sr, deps) {
        var ARS = (deps && deps.AudioRangeSource) || root.AudioRangeSource;
        if (!ARS) { var miss = new Error('AudioRangeSource is not loaded'); miss.code = 'AE_NO_SOURCE'; throw miss; }
        var range;
        try {
            range = await ARS.open(file, { FFmpegProcessor: deps.FFmpegProcessor });
        } catch (e) {
            if (e && e.code === 'ARS_NO_AUDIO') e.code = 'AE_NO_AUDIO';
            throw e;
        }
        var ch = range.info.channels;
        var block = READ_BLOCK_S * sr;
        var held = [];          // [{start, chans}] по порядку
        var next = 0;           // следующий непрочитанный отсчёт
        var total = null;

        async function readBlock() {
            var chans;
            try {
                chans = await range.read(next, block, sr, ch);
            } catch (e) {
                if (e && e.code === 'ARS_DECODE') e.code = 'AE_DECODE';
                throw e;
            }
            var got = chans[0].length;
            if (got > 0) held.push({ start: next, chans: chans });
            next += got;
            if (got < block) total = next;
        }

        return {
            kind: 'ffmpeg', sampleRate: sr, channels: ch,
            fill: async function (upTo) {
                while (total === null && next < upTo) await readBlock();
            },
            total: function () { return total; },
            slice: function (a, b) {
                var n = Math.max(0, b - a), out = [], c, i;
                for (c = 0; c < ch; c++) out.push(new Float32Array(n));
                for (i = 0; i < held.length; i++) {
                    var h = held[i], hEnd = h.start + h.chans[0].length;
                    var lo = Math.max(a, h.start), hi = Math.min(b, hEnd);
                    if (hi <= lo) continue;
                    for (c = 0; c < ch; c++) out[c].set(h.chans[c].subarray(lo - h.start, hi - h.start), lo - a);
                }
                return out;
            },
            drop: function (before) {
                while (held.length && held[0].start + held[0].chans[0].length <= before) held.shift();
            },
            close: function () { held = []; return range.close(); }
        };
    }

    /**
     * Прогнать источник через граф окнами.
     *
     * o.source        — bufferSource() / ffmpegSource()
     * o.OAC           — OfflineAudioContext
     * o.buildChain    — (ctx, audioBuffer) → AudioBufferSourceNode, уже
     *                   подключённый через весь граф к ctx.destination
     * o.lead          — задержка графа в отсчётах (упреждение компрессора)
     * o.preFrames     — преролл в отсчётах
     * o.windowFrames  — длина окна
     * o.onBlock       — async (каналы окна) → записать
     * o.onProgress    — (готово отсчётов, всего | null)
     * o.isCancelled   — () → bool
     */
    async function run(o) {
        var src = o.source, sr = src.sampleRate, ch = src.channels;
        var win = Math.max(GRID, o.windowFrames | 0);
        var pre = Math.max(0, o.preFrames | 0);
        var cut = Math.max(0, o.lead | 0);
        var windows = 0, s = 0;

        for (;;) {
            if (o.isCancelled && o.isCancelled()) throw abortErr();
            await src.fill(s + win + cut);
            var total = src.total();
            var e = s + win;
            if (total !== null) {
                if (s >= total) break;
                e = Math.min(e, total);
            }
            var w = windowAt(s, e, pre, cut);
            /* Вход не обрезается по концу окна: последние `cut` отсчётов
               рендера считаются против настоящего продолжения записи. */
            var input = src.slice(w.from, total !== null ? Math.min(e + cut, total) : e + cut);
            var ctx = new o.OAC(ch, w.frames, sr);
            var buf = ctx.createBuffer(ch, Math.max(1, input[0].length), sr);
            for (var c = 0; c < ch; c++) if (input[c].length) buf.copyToChannel(input[c], c);
            input = null;
            var node = o.buildChain(ctx, buf);
            node.start(0);
            var rendered = await ctx.startRendering();
            if (o.isCancelled && o.isCancelled()) throw abortErr();
            var block = [];
            for (c = 0; c < ch; c++) block.push(rendered.getChannelData(c).subarray(w.skip, w.skip + (e - s)));
            await o.onBlock(block);
            windows++;
            s = e;
            src.drop(Math.max(0, Math.floor((s - pre) / GRID) * GRID));
            if (o.onProgress) o.onProgress(s, total);
            if (total !== null && s >= total) break;
        }
        return { windows: windows, frames: s, channels: ch, sampleRate: sr };
    }

    root.AEManualWindows = {
        GRID: GRID,
        READ_BLOCK_S: READ_BLOCK_S,
        windowAt: windowAt,
        plan: plan,
        prerollSec: prerollSec,
        FloorStats: FloorStats,
        bufferSource: bufferSource,
        ffmpegSource: ffmpegSource,
        run: run
    };
})(typeof window !== 'undefined' ? window : globalThis);
